import { AnimatePresence, Reorder, motion, useDragControls } from 'motion/react'
import { GripVertical, Lock, LockOpen, Repeat, Trash2 } from 'lucide-react'
import { PlayButton } from '@/components/player/PlayButton'
import { usePlayerState, type PlayerTrack } from '@/components/player/PlayerProvider'
import { SpotlightCard } from '@/components/dashboard/fx/SpotlightCard'
import { EASE } from '@/components/dashboard/fx/motion'
import { Cover, Equalizer, PositionMarker, TagChip } from '@/components/build/parts'
import { TAG_META, formatClock, itemTrack, type BuildItem } from '@/components/build/types'
import type { Entry, Frame } from '@/hooks/usePlaylistBuild'

interface SlotCardProps {
  entry: Entry
  index: number
  count: number
  frame: Frame | undefined
  queue: PlayerTrack[]
  queueIndex: number
  name: string
  open: boolean
  onToggleOpen: () => void
  onLock: () => void
  onRemove: () => void
  onSwap: (choice: BuildItem) => void
}

function IconButton({ label, onClick, active, danger, children }: { label: string; onClick: () => void; active?: boolean; danger?: boolean; children: React.ReactNode }) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-label={label}
      aria-pressed={active}
      title={label}
      className={`flex size-9 items-center justify-center rounded-full transition-colors ${
        active ? 'bg-primary/15 text-primary' : `text-muted-foreground hover:bg-muted ${danger ? 'hover:text-destructive' : 'hover:text-foreground'}`
      }`}
    >
      {children}
    </button>
  )
}

export function SlotCard({ entry, index, count, frame, queue, queueIndex, name, open, onToggleOpen, onLock, onRemove, onSwap }: SlotCardProps) {
  const controls = useDragControls()
  const player = usePlayerState()
  const { item, locked, alternatives } = entry
  const playing = Boolean(item && player.track?.key === itemTrack(item).key && player.status === 'playing')

  return (
    <Reorder.Item
      value={entry}
      dragListener={false}
      dragControls={controls}
      as="li"
      whileDrag={{ scale: 1.02, zIndex: 20 }}
      className="relative list-none"
    >
      {/* The entrance lives on an inner wrapper: Reorder.Item owns its own
          layout and y while dragging, and animating those on the item itself
          makes the row hard to move. */}
      <motion.div
        initial={{ opacity: 0, scale: 0.96 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ duration: 0.5, ease: EASE, delay: Math.min(index, 10) * 0.04 }}
      >
        <SpotlightCard className={`overflow-visible rounded-2xl transition-shadow ${locked ? 'ring-1 ring-primary/60' : ''} ${playing ? 'shadow-lg shadow-primary/20' : ''}`}>
          <div className="flex items-center gap-2 p-3 sm:gap-4 sm:p-4">
            <button
              type="button"
              aria-label="Drag to reorder"
              onPointerDown={(event) => controls.start(event)}
              className="hidden shrink-0 cursor-grab touch-none rounded-md p-1 text-muted-foreground/60 hover:text-foreground active:cursor-grabbing sm:block"
            >
              <GripVertical className="size-4" />
            </button>

            <div className="w-14 shrink-0 text-center sm:w-20">
              <div className="font-mono text-[10px] uppercase tracking-[0.18em] text-primary">{frame?.role ?? ''}</div>
              <div className="mt-0.5 font-display text-lg font-semibold tabular-nums leading-none sm:text-xl">{formatClock(frame?.startsAt ?? 0)}</div>
            </div>

            {item ? (
              <>
                <div className="relative size-14 shrink-0 overflow-hidden rounded-xl ring-1 ring-border sm:size-16">
                  <Cover item={item} />
                  <PlayButton variant="overlay" track={itemTrack(item)} queue={{ tracks: queue, index: queueIndex }} />
                </div>

                <div className="flex min-w-0 flex-1 flex-col gap-1.5">
                  <div className="flex items-center gap-2">
                    <AnimatePresence mode="wait" initial={false}>
                      <motion.span
                        key={item.key}
                        initial={{ opacity: 0, y: 8 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: -8 }}
                        transition={{ duration: 0.25 }}
                        className="truncate font-display text-lg font-semibold tracking-tight sm:text-xl"
                      >
                        {item.title}
                      </motion.span>
                    </AnimatePresence>
                    {playing && <Equalizer />}
                  </div>
                  <span className="truncate text-sm text-muted-foreground">{item.artists.join(', ')}</span>
                  <div className="flex flex-wrap items-center gap-x-3 gap-y-1.5">
                    <span title={TAG_META[item.tag].blurb(name)}>
                      <TagChip tag={item.tag} />
                    </span>
                    <PositionMarker item={item} index={index} count={count} />
                  </div>
                </div>
              </>
            ) : (
              <button
                type="button"
                onClick={onToggleOpen}
                className="flex h-16 flex-1 items-center justify-center rounded-xl border border-dashed border-border text-sm text-muted-foreground transition-colors hover:border-primary/50 hover:text-foreground"
              >
                Empty slot -- pick a song for it
              </button>
            )}

            <div className="flex shrink-0 flex-col items-center gap-0.5 sm:flex-row">
              {item && (
                <IconButton label={locked ? 'Unlock this slot' : 'Lock this slot so shuffling keeps it'} onClick={onLock} active={locked}>
                  {locked ? <Lock className="size-4" /> : <LockOpen className="size-4" />}
                </IconButton>
              )}
              <IconButton label="Swap for another song" onClick={onToggleOpen} active={open}>
                <Repeat className="size-4" />
              </IconButton>
              {item && (
                <IconButton label="Remove this song" onClick={onRemove} danger>
                  <Trash2 className="size-4" />
                </IconButton>
              )}
            </div>
          </div>

          <AnimatePresence initial={false}>
            {open && (
              <motion.div
                initial={{ height: 0, opacity: 0 }}
                animate={{ height: 'auto', opacity: 1 }}
                exit={{ height: 0, opacity: 0 }}
                transition={{ duration: 0.4, ease: EASE }}
                className="overflow-hidden"
              >
                <div className="border-t border-border/70 p-3 sm:p-4">
                  <p className="mb-3 font-mono text-[10px] uppercase tracking-[0.2em] text-muted-foreground">Swap in something else for this spot</p>
                  {/* Alternatives are a swipeable strip on phones and a grid everywhere else.
                      There are at most 8, and a horizontal strip can't be reached with a
                      mouse wheel (only a trackpad or a visible scrollbar) -- on desktop the
                      cards were clipped with no way to get to them. */}
                  {alternatives.length === 0 ? (
                    <p className="text-sm text-muted-foreground">Nothing else fits here.</p>
                  ) : (
                    <ul className="flex snap-x gap-3 overflow-x-auto pb-2 sm:grid sm:grid-cols-2 sm:overflow-visible sm:pb-0 xl:grid-cols-3">
                      {alternatives.map((alt) => (
                        <li key={alt.key} className="flex w-60 shrink-0 snap-start items-center gap-3 rounded-xl border border-border/70 bg-background/60 p-2.5 sm:w-auto sm:shrink">
                          <div className="relative size-11 shrink-0 overflow-hidden rounded-lg">
                            <Cover item={alt} />
                            <PlayButton variant="overlay" track={itemTrack(alt)} />
                          </div>
                          <div className="flex min-w-0 flex-1 flex-col gap-1">
                            <span className="truncate text-sm font-medium">{alt.title}</span>
                            <span className="truncate text-xs text-muted-foreground">{alt.artists.join(', ')}</span>
                            <TagChip tag={alt.tag} className="w-fit" />
                          </div>
                          <button
                            type="button"
                            onClick={() => onSwap(alt)}
                            className="shrink-0 rounded-full bg-primary px-3 py-1.5 text-xs font-medium text-primary-foreground transition-transform hover:scale-105"
                          >
                            Use
                          </button>
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </SpotlightCard>
      </motion.div>
    </Reorder.Item>
  )
}
